import { FlexBox, Icon, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import type { MetaConversation } from 'lib-shared-legacy-types';
import { useCallback, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { getMetaConversationName } from 'app/utils/conversation';
import {
  ConversationDetails,
  ConversationDetailsLayout,
} from 'components/conversation/conversationDetails';
import { TypingType } from 'components/conversation/reducers/typing';
import { CommandNameInternal, useCommandHandler } from 'utils/commands';
import { useConversationHasMissingRequiredCustomFieldsInAcw } from 'utils/conversation';
import { createAppUrl } from 'utils/url/createAppUrl';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from './conversationListItem.module.scss';
import type {
  DispatchPropsT as SidebarMenuDispatchPropsT,
  StatePropsT as SidebarMenuStatePropsT,
} from './sidebarMenu';

type OwnProps = {
  listIndex: number;
  isViewingConversation: boolean;
  metaConversation: MetaConversation;
  typing?: TypingType;
  onToggleNavigation?: () => void;
};

type StatePropsT = Pick<
  SidebarMenuStatePropsT,
  'isMenuOpen' | 'conversationsLastSearchParams'
>;

type DispatchPropsT = Pick<
  SidebarMenuDispatchPropsT,
  'typingRemove' | 'conversationsRemove' | 'wsRequest'
>;

type PropsT = StatePropsT & DispatchPropsT & OwnProps;

export const ConversationListItem = ({
  listIndex,
  conversationsLastSearchParams,
  isViewingConversation,
  metaConversation,
  isMenuOpen,
  typing,
  conversationsRemove,
  wsRequest,
  onToggleNavigation,
}: PropsT): JSX.Element => {
  const navigate = useNavigate();
  const { conversation, isAssignedToMe } = metaConversation;
  const csid = conversation.id ?? 0;
  const hasMissingRequiredCustomFieldsInAcw =
    useConversationHasMissingRequiredCustomFieldsInAcw({ csid });

  const removeConversation = useCallback(() => {
    conversationsRemove(csid);

    wsRequest({
      type: 'connection:unsubscribe',
      params: {
        type: 'conversation',
        csid,
      },
    });
    // Check if we are closing the conversation we are looking at
    if (isViewingConversation) {
      navigate(
        createAppUrl({
          path: '/conversations',
          params: { search: conversationsLastSearchParams },
        }),
      );
    }
  }, [
    conversationsRemove,
    csid,
    wsRequest,
    isViewingConversation,
    navigate,
    conversationsLastSearchParams,
  ]);

  const extraContent = useMemo(() => {
    const isClosed = conversation.is_closed && conversation.status === 'closed';
    const isPending = conversation.status === 'pending';

    if (isClosed && hasMissingRequiredCustomFieldsInAcw) {
      return (
        <Tooltip
          wrap="span"
          wrapProps={{ className: styles.tooltipWrapper }}
          content="Missing required attributes"
        >
          <Icon
            className={styles.warningIcon}
            name="exclamation-triangle-filled"
          />
        </Tooltip>
      );
    }

    if (isClosed || isPending || !isAssignedToMe) {
      return (
        <Tooltip
          wrap="span"
          wrapProps={{ className: styles.tooltipWrapper }}
          content="Remove"
        >
          <button
            type="button"
            aria-label="Remove conversation"
            className={styles.closeConversationButton}
            onClick={() => removeConversation()}
          >
            <Icon name="close-circle-filled" />
          </button>
        </Tooltip>
      );
    }

    return null;
  }, [
    conversation.is_closed,
    conversation.status,
    hasMissingRequiredCustomFieldsInAcw,
    isAssignedToMe,
    removeConversation,
  ]);

  const detailsLayout = isMenuOpen
    ? ConversationDetailsLayout.Sidebar
    : ConversationDetailsLayout.SidebarCollapsed;

  const rootClass = classnames({
    [styles.root]: true,
    [styles.rootCollapsed]: !isMenuOpen,
    [styles.rootHasExtra]: !!extraContent,
  });

  const isInFirst9 = listIndex >= 0 || listIndex < 9;

  return (
    <li className={rootClass}>
      {isInFirst9 && (
        <ConversationListItemCommandHandler index={listIndex + 1} csid={csid} />
      )}

      <NavLink
        className={({ isActive }) =>
          classnames(styles.link, { [styles.linkActive]: isActive })
        }
        to={`/conversation/${conversation.id}`}
        onClick={() => onToggleNavigation?.()}
      >
        <ConversationDetails
          metaConversation={metaConversation}
          conversationName={getMetaConversationName(metaConversation)}
          layout={detailsLayout}
          isTyping={typing?.csid === conversation.id}
          newMessageCount={metaConversation.unread}
        />
      </NavLink>

      {extraContent && (
        <FlexBox className={styles.extra}>{extraContent}</FlexBox>
      )}
    </li>
  );
};

type ConversationListItemCommandHandlerProps = {
  index: number;
  csid: number;
};

/**
 * Represents the component for navigating to an active conversation with a
 * command.
 */
const ConversationListItemCommandHandler = ({
  index,
  csid,
}: ConversationListItemCommandHandlerProps): null => {
  const navigate = useAppNavigate();
  const name =
    `conversation:goToActiveConversation${index}` as CommandNameInternal;

  useCommandHandler({
    name,
    actionFn: () => {
      navigate({
        path: '/conversation/:csid',
        params: { path: { csid: csid.toString() } },
      });
    },
  });

  return null;
};
